import Link from "next/link";
import { useRouter } from "next/router";
import type { SupportedLocale } from "@/lib/i18n";
import { getPublicPathname } from "@/lib/publicPath";
import styles from "@/styles/Breadcrumbs.module.css";

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://12gobricks.com";

function buildAbsoluteUrl(path: string): string {
  const base = BASE_URL.replace(/\/+$/, "");
  const p = (path || "/").replace(/\/+$/, "") || "";
  return p === "" || p === "/" ? base : `${base}${p.startsWith("/") ? p : `/${p}`}`;
}

type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  locale: SupportedLocale;
};

export function Breadcrumbs({ items, locale }: BreadcrumbsProps) {
  const router = useRouter();
  if (items.length === 0) return null;

  const currentPath = getPublicPathname(router.asPath || "/");

  const schemaBreadcrumb: {
    "@context": "https://schema.org";
    "@type": "BreadcrumbList";
    itemListElement: Array<{
      "@type": "ListItem";
      position: number;
      item: { "@id": string; name: string };
    }>;
  } = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => {
      const position = i + 1;
      const url = item.href ? buildAbsoluteUrl(item.href) : buildAbsoluteUrl(currentPath);
      return {
        "@type": "ListItem" as const,
        position,
        item: { "@id": url, name: item.label },
      };
    }),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schemaBreadcrumb),
        }}
      />
      <nav aria-label="Breadcrumb" className={styles.breadcrumbs}>
        <ol className={styles.breadcrumbsList} itemScope itemType="https://schema.org/BreadcrumbList">
        {items.map((item, i) => (
          <li
            key={i}
            className={styles.breadcrumbsItem}
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            {i > 0 && (
              <span className={styles.breadcrumbsSep} aria-hidden>
                /
              </span>
            )}
            {item.href ? (
              <Link
                href={item.href}
                className={styles.breadcrumbsLink}
                itemProp="item"
              >
                <span itemProp="name">{item.label}</span>
              </Link>
            ) : (
              <span
                className={styles.breadcrumbsCurrent}
                aria-current="page"
                itemProp="item"
              >
                <span itemProp="name">{item.label}</span>
              </span>
            )}
            <meta itemProp="position" content={String(i + 1)} />
          </li>
        ))}
      </ol>
    </nav>
    </>
  );
}
